import { createFileRoute } from "@tanstack/react-router"
import { MapCanvas } from "@/components/map/map-canvas"
import { MapSidebar } from "@/components/map/map-sidebar"
import { MapControls } from "@/components/map/map-controls"
import { PageLoader } from "@/components/ui/page-loader"
import { PageError } from "@/components/ui/page-error"
import { PanelRightOpen, PanelRightClose } from "lucide-react"
import { useMapWorkbenchData } from "@/features/workbench/hooks/use-map-workbench-data"
import { fetchSites } from "@/lib/api/sites"
import { fetchAlerts } from "@/lib/api/alerts"
import { fetchInsarDataset } from "@/lib/api/insar"

export const Route = createFileRoute("/_authenticated/$siteSlug/map")({
  loader: async ({ context: { queryClient }, params: { siteSlug } }) => {
    const sites = await queryClient.ensureQueryData({
      queryKey: ["sites"],
      queryFn: fetchSites,
    })

    void queryClient.ensureQueryData({
      queryKey: ["alerts", { siteSlug, active: true }],
      queryFn: () => fetchAlerts({ siteSlug, active: true }),
    })

    const site = sites.find((s) => s.slug === siteSlug)
    if (site?.insar_dataset) {
      void queryClient.ensureQueryData({
        queryKey: ["insar-dataset", site.insar_dataset],
        queryFn: () => fetchInsarDataset(site.insar_dataset!),
      })
    }
  },
  component: MapPage,
})

function MapPage() {
  const { siteSlug } = Route.useParams()
  const {
    sitesLoading, sitesError, sitesErrorObj,
    site, latitude, longitude,
    tracks, alerts,
    mapStyle, setMapStyle,
    sidebarOpen, setSidebarOpen,
  } = useMapWorkbenchData({ siteSlug })

  if (sitesLoading) {
    return <PageLoader message="Loading site..." />
  }

  if (sitesError) {
    return <PageError error={sitesErrorObj} />
  }

  return (
    <div className="relative flex h-full flex-col md:flex-row">
      {/* Map area - full width on mobile */}
      <div className="relative flex-1 min-h-0">
        <MapControls style={mapStyle} onStyleChange={setMapStyle} />
        <MapCanvas
          siteSlug={siteSlug}
          latitude={latitude}
          longitude={longitude}
          insarDatasetSlug={site?.insar_dataset}
          tracks={tracks}
          styleKey={mapStyle}
        />

        {/* Mobile toggle button for sidebar */}
        <button
          onClick={() => setSidebarOpen((o) => !o)}
          className="absolute bottom-4 right-4 z-10 flex md:hidden h-10 w-10 items-center justify-center rounded-[var(--radius-card)] bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] shadow-lg border border-[var(--color-border-subtle)] transition-colors hover:bg-[var(--color-bg-tertiary)] hover:text-[var(--color-text-primary)]"
        >
          {sidebarOpen ? (
            <PanelRightClose className="h-4 w-4" />
          ) : (
            <PanelRightOpen className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Desktop sidebar - always visible */}
      <div className="hidden md:block">
        <MapSidebar siteSlug={siteSlug} tracks={tracks} alerts={alerts} />
      </div>

      {/* Mobile bottom sheet */}
      <div
        className={`md:hidden absolute inset-x-0 bottom-0 z-20 transform transition-transform duration-[var(--duration-layout)] ease-[var(--ease-spring)] ${
          sidebarOpen ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="max-h-[60vh] overflow-auto rounded-t-[var(--radius-panel)] border-t border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] shadow-xl">
          {/* Drag handle visual */}
          <div className="flex justify-center py-2">
            <div className="h-1 w-8 rounded-full bg-[var(--color-border-strong)]" />
          </div>
          <MapSidebar siteSlug={siteSlug} tracks={tracks} alerts={alerts} />
        </div>
      </div>
    </div>
  )
}
